/**
 * K3 Lease Section
 *
 * Leasingavtal enligt K3 kapitel 20
 * Hanterar både operationella och finansiella leasingavtal
 */

import { useState, useEffect } from "react";
import {
  FileKey,
  Plus,
  Trash2,
  Info,
  ChevronDown,
  ChevronUp,
  Car,
  Building,
} from "lucide-react";
import { formatNumber } from "@/lib/format";
import type { UserInput } from "@/lib/api";

interface OperationalLease {
  id: string;
  description: string;
  annualCost: number;
  remainingPeriod: number; // months
  totalCommitment: number;
}

interface FinancialLease {
  id: string;
  description: string;
  acquisitionValue: number;
  accumulatedDepreciation: number;
  remainingDebt: number;
  interestRate: number;
}

interface K3LeaseSectionProps {
  userInput: UserInput;
  onChange: (leasing: UserInput["leasing"]) => void;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export function K3LeaseSection({ userInput, onChange }: K3LeaseSectionProps) {
  const [operationalLeases, setOperationalLeases] = useState<OperationalLease[]>(
    () =>
      userInput.leasing?.operationalLeases?.map((l) => ({
        ...l,
        id: l.id || generateId(),
      })) || []
  );
  const [financialLeases, setFinancialLeases] = useState<FinancialLease[]>(
    () =>
      userInput.leasing?.financialLeases?.map((l) => ({
        ...l,
        id: l.id || generateId(),
      })) || []
  );
  const [showOperational, setShowOperational] = useState(operationalLeases.length > 0);
  const [showFinancial, setShowFinancial] = useState(financialLeases.length > 0);

  // Sync changes to parent
  useEffect(() => {
    const leasing: UserInput["leasing"] = {
      operationalLeases:
        operationalLeases.length > 0
          ? operationalLeases.map((l) => ({
              id: l.id,
              description: l.description,
              annualCost: l.annualCost,
              remainingPeriod: l.remainingPeriod,
              totalCommitment: l.totalCommitment,
            }))
          : undefined,
      financialLeases:
        financialLeases.length > 0
          ? financialLeases.map((l) => ({
              id: l.id,
              description: l.description,
              acquisitionValue: l.acquisitionValue,
              accumulatedDepreciation: l.accumulatedDepreciation,
              remainingDebt: l.remainingDebt,
              interestRate: l.interestRate,
            }))
          : undefined,
    };
    onChange(leasing);
  }, [operationalLeases, financialLeases, onChange]);

  // Operational lease handlers
  const addOperationalLease = () => {
    const newLease: OperationalLease = {
      id: generateId(),
      description: "",
      annualCost: 0,
      remainingPeriod: 12,
      totalCommitment: 0,
    };
    setOperationalLeases([...operationalLeases, newLease]);
    setShowOperational(true);
  };

  const updateOperationalLease = (id: string, updates: Partial<OperationalLease>) => {
    setOperationalLeases(
      operationalLeases.map((l) => (l.id === id ? { ...l, ...updates } : l))
    );
  };

  const removeOperationalLease = (id: string) => {
    setOperationalLeases(operationalLeases.filter((l) => l.id !== id));
  };

  // Financial lease handlers
  const addFinancialLease = () => {
    const newLease: FinancialLease = {
      id: generateId(),
      description: "",
      acquisitionValue: 0,
      accumulatedDepreciation: 0,
      remainingDebt: 0,
      interestRate: 0,
    };
    setFinancialLeases([...financialLeases, newLease]);
    setShowFinancial(true);
  };

  const updateFinancialLease = (id: string, updates: Partial<FinancialLease>) => {
    setFinancialLeases(
      financialLeases.map((l) => (l.id === id ? { ...l, ...updates } : l))
    );
  };

  const removeFinancialLease = (id: string) => {
    setFinancialLeases(financialLeases.filter((l) => l.id !== id));
  };

  // Calculate totals
  const totalOperationalCommitment = operationalLeases.reduce(
    (sum, l) => sum + l.totalCommitment,
    0
  );
  const totalFinancialDebt = financialLeases.reduce((sum, l) => sum + l.remainingDebt, 0);
  const totalFinancialAssets = financialLeases.reduce(
    (sum, l) => sum + (l.acquisitionValue - l.accumulatedDepreciation),
    0
  );

  const hasAnyLeases = operationalLeases.length > 0 || financialLeases.length > 0;

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <FileKey className="text-purple-600" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Leasingavtal</h3>
            <p className="text-sm text-gray-500">K3 kap 20</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
          K3
        </span>
      </div>

      {/* Info */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-purple-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-purple-800">
            <p>
              K3 skiljer mellan <strong>operationella</strong> och{" "}
              <strong>finansiella</strong> leasingavtal. Finansiella avtal redovisas i
              balansräkningen som tillgång och skuld.
            </p>
          </div>
        </div>
      </div>

      {/* Operational Leases */}
      <div className="mb-6">
        <div
          className="flex items-center justify-between cursor-pointer py-2"
          onClick={() => setShowOperational(!showOperational)}
        >
          <div className="flex items-center gap-2">
            {showOperational ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <Car size={18} className="text-gray-500" />
            <h4 className="font-medium text-gray-800">Operationella leasingavtal</h4>
            <span className="text-xs text-gray-500">({operationalLeases.length})</span>
          </div>
          {totalOperationalCommitment > 0 && (
            <span className="text-sm text-gray-600">
              Totalt åtagande: {formatNumber(totalOperationalCommitment)} kr
            </span>
          )}
        </div>

        {showOperational && (
          <div className="mt-3 space-y-3">
            <p className="text-xs text-gray-500">
              Operationella leasingavtal redovisas som löpande kostnad. Upplys om framtida
              åtaganden.
            </p>

            {operationalLeases.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                Inga operationella leasingavtal registrerade.
              </p>
            ) : (
              <div className="space-y-3">
                {operationalLeases.map((lease) => (
                  <div
                    key={lease.id}
                    className="border border-gray-200 rounded-lg p-3 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <input
                        type="text"
                        value={lease.description}
                        onChange={(e) =>
                          updateOperationalLease(lease.id, { description: e.target.value })
                        }
                        placeholder="Beskrivning (t.ex. Tjänstebil, Kontorsutrustning)"
                        className="input flex-1 mr-2"
                      />
                      <button
                        type="button"
                        onClick={() => removeOperationalLease(lease.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Årskostnad
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={lease.annualCost || ""}
                            onChange={(e) =>
                              updateOperationalLease(lease.id, {
                                annualCost: Number(e.target.value) || 0,
                              })
                            }
                            className="input w-full pr-8 text-sm"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            kr
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Återstående (mån)
                        </label>
                        <input
                          type="number"
                          value={lease.remainingPeriod || ""}
                          onChange={(e) =>
                            updateOperationalLease(lease.id, {
                              remainingPeriod: Number(e.target.value) || 0,
                            })
                          }
                          className="input w-full text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Totalt åtagande
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={lease.totalCommitment || ""}
                            onChange={(e) =>
                              updateOperationalLease(lease.id, {
                                totalCommitment: Number(e.target.value) || 0,
                              })
                            }
                            className="input w-full pr-8 text-sm"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            kr
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={addOperationalLease}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
            >
              <Plus size={14} />
              Lägg till operationellt leasingavtal
            </button>
          </div>
        )}
      </div>

      {/* Financial Leases */}
      <div>
        <div
          className="flex items-center justify-between cursor-pointer py-2"
          onClick={() => setShowFinancial(!showFinancial)}
        >
          <div className="flex items-center gap-2">
            {showFinancial ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <Building size={18} className="text-gray-500" />
            <h4 className="font-medium text-gray-800">Finansiella leasingavtal</h4>
            <span className="text-xs text-gray-500">({financialLeases.length})</span>
          </div>
          {totalFinancialDebt > 0 && (
            <span className="text-sm text-gray-600">
              Kvarvarande skuld: {formatNumber(totalFinancialDebt)} kr
            </span>
          )}
        </div>

        {showFinancial && (
          <div className="mt-3 space-y-3">
            <p className="text-xs text-gray-500">
              Finansiella leasingavtal redovisas som tillgång (avskrivs) och skuld i
              balansräkningen.
            </p>

            {financialLeases.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
                Inga finansiella leasingavtal registrerade.
              </p>
            ) : (
              <div className="space-y-3">
                {financialLeases.map((lease) => (
                  <div
                    key={lease.id}
                    className="border border-gray-200 rounded-lg p-3 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <input
                        type="text"
                        value={lease.description}
                        onChange={(e) =>
                          updateFinancialLease(lease.id, { description: e.target.value })
                        }
                        placeholder="Beskrivning (t.ex. Maskin, Fastighet)"
                        className="input flex-1 mr-2"
                      />
                      <button
                        type="button"
                        onClick={() => removeFinancialLease(lease.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Anskaffningsvärde
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={lease.acquisitionValue || ""}
                            onChange={(e) =>
                              updateFinancialLease(lease.id, {
                                acquisitionValue: Number(e.target.value) || 0,
                              })
                            }
                            className="input w-full pr-8 text-sm"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            kr
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Ackumulerad avskrivning
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={lease.accumulatedDepreciation || ""}
                            onChange={(e) =>
                              updateFinancialLease(lease.id, {
                                accumulatedDepreciation: Number(e.target.value) || 0,
                              })
                            }
                            className="input w-full pr-8 text-sm"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            kr
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Kvarvarande skuld
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={lease.remainingDebt || ""}
                            onChange={(e) =>
                              updateFinancialLease(lease.id, {
                                remainingDebt: Number(e.target.value) || 0,
                              })
                            }
                            className="input w-full pr-8 text-sm"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            kr
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Ränta (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={lease.interestRate || ""}
                          onChange={(e) =>
                            updateFinancialLease(lease.id, {
                              interestRate: Number(e.target.value) || 0,
                            })
                          }
                          className="input w-full text-sm"
                        />
                      </div>
                    </div>

                    {/* Calculated book value */}
                    <div className="bg-gray-50 rounded-lg p-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Redovisat värde:</span>
                        <span className="font-medium">
                          {formatNumber(
                            lease.acquisitionValue - lease.accumulatedDepreciation
                          )}{" "}
                          kr
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={addFinancialLease}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
            >
              <Plus size={14} />
              Lägg till finansiellt leasingavtal
            </button>
          </div>
        )}
      </div>

      {/* Summary */}
      {hasAnyLeases && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Sammanfattning</h4>
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            {operationalLeases.length > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Operationella åtaganden ({operationalLeases.length} avtal):
                </span>
                <span className="font-medium">
                  {formatNumber(totalOperationalCommitment)} kr
                </span>
              </div>
            )}
            {financialLeases.length > 0 && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    Finansiella tillgångar ({financialLeases.length} avtal):
                  </span>
                  <span className="font-medium">
                    {formatNumber(totalFinancialAssets)} kr
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Finansiella skulder:</span>
                  <span className="font-medium">{formatNumber(totalFinancialDebt)} kr</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Tip if no leases */}
      {!hasAnyLeases && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500">
            <strong>Tips:</strong> Om företaget har leasingavtal för t.ex. bilar, maskiner
            eller lokaler, registrera dem här för korrekt redovisning och notupplysningar.
          </p>
        </div>
      )}
    </div>
  );
}
